import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import CloseButton from '@/assets/svgs/CloseButton';
import { LogoutModal } from '@/components/modals/LogoutModal';
import { useSeiBalance } from '@hooks';
import { useWallet } from '@/contexts/WalletContext';
import {
  generateAvatarFromAddress,
  generateNameFromAddress,
  formatWalletAddress,
} from '@/shared/utils';

// Import header assets
import goldIcon from '@/assets/header/gold.webp';
import seiIcon from '@/assets/header/sei.webp';
import energyIcon from '@/assets/header/energy.webp';
import pointIcon from '@/assets/header/point.webp';

import type { UserProfile } from '@types';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile;
  onLogoutModalChange?: (isOpen: boolean) => void;
}

type ModalView = 'profile' | 'qrcode';

const WALLET_LOGOS: Record<string, { name: string; gradient: string }> = {
  'sei-global': {
    name: 'Sei Global',
    gradient: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
  },
  metamask: {
    name: 'MetaMask',
    gradient: 'linear-gradient(135deg, #E2761B 0%, #F6851B 100%)',
  },
  compass: {
    name: 'Compass',
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
  },
};

export const ProfileModal = ({
  isOpen,
  onClose,
  user,
  onLogoutModalChange,
}: ProfileModalProps) => {
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<ModalView>('profile');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { logout } = useWallet();
  const { balance, refetch, isLoading: balanceLoading } = useSeiBalance();

  const handleCopyAddress = async () => {
    if (user?.address) {
      await navigator.clipboard.writeText(user.address);
      setCopied(true);
      toast.success('Address copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRefreshBalance = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleClose = () => {
    setView('profile');
    onClose();
  };

  const handleLogout = () => {
    onClose();
    setIsLogoutModalOpen(true);
    onLogoutModalChange?.(true);
  };

  const handleCloseLogoutModal = () => {
    setIsLogoutModalOpen(false);
    onLogoutModalChange?.(false);
  };

  const handleConfirmLogout = () => {
    setIsLoggingOut(true);
    logout();
    setIsLoggingOut(false);
    setIsLogoutModalOpen(false);
    toast.success('Logged out successfully!');
  };

  const walletInfo = user?.walletType ? WALLET_LOGOS[user.walletType] : null;
  const avatarUrl = user?.address
    ? generateAvatarFromAddress(user.address, 80)
    : null;
  const displayName = user?.address
    ? generateNameFromAddress(user.address)
    : 'Defender';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[101] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none px-4">
            <motion.div
              className="relative w-full max-w-[360px] flex flex-col isolate rounded-2xl pointer-events-auto overflow-hidden"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              style={{
                background:
                  'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                boxShadow: `
                  0 0 40px rgba(139, 92, 246, 0.2),
                  0 20px 60px rgba(0, 0, 0, 0.5),
                  inset 0 1px 0 rgba(255, 255, 255, 0.1)
                `,
              }}
            >
              {/* Decorative glow effects */}
              <div
                className="absolute -top-20 -left-20 w-40 h-40 rounded-full pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
                }}
              />
              <div
                className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle, rgba(251, 191, 36, 0.1) 0%, transparent 70%)',
                }}
              />

              <AnimatePresence mode="wait">
                {view === 'profile' ? (
                  <motion.div
                    key="profile"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Header */}
                    <div
                      className="relative px-5 py-4 flex items-center justify-between"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)',
                        borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
                      }}
                    >
                      <h2 className="font-lilita text-xl text-white [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                        Defender Profile
                      </h2>
                      <motion.div
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleClose}
                        className="cursor-pointer"
                      >
                        <CloseButton />
                      </motion.div>
                    </div>

                    <div className="p-5">
                      {/* Avatar & Name Section */}
                      <div className="flex flex-col items-center mb-5">
                        <motion.div
                          className="relative"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 20,
                          }}
                        >
                          {/* Avatar glow ring */}
                          <div
                            className="absolute -inset-1 rounded-xl animate-pulse"
                            style={{
                              background:
                                'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 50%, #8B5CF6 100%)',
                              opacity: 0.5,
                            }}
                          />
                          <div
                            className="relative w-20 h-20 rounded-xl overflow-hidden"
                            style={{
                              border: '3px solid rgba(255, 255, 255, 0.2)',
                              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                            }}
                          >
                            {avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt={displayName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                                <span className="text-white font-bold text-2xl">
                                  ?
                                </span>
                              </div>
                            )}
                          </div>
                        </motion.div>

                        <h3 className="font-lilita text-2xl text-white mt-3 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                          {displayName}
                        </h3>

                        {/* Wallet Badge */}
                        {walletInfo && (
                          <div
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full mt-2"
                            style={{
                              background: walletInfo.gradient,
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                            }}
                          >
                            <svg
                              className="w-3.5 h-3.5 text-white"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M21 18v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1h-9a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h9zm-9-2h10V8H12v8zm4-2.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                            </svg>
                            <span className="text-xs font-bold text-white">
                              {walletInfo.name}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* SEI Balance Card */}
                      <div
                        className="relative p-4 rounded-xl mb-4 overflow-hidden"
                        style={{
                          background:
                            'linear-gradient(145deg, #2d1b4e 0%, #1a1035 100%)',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          boxShadow:
                            'inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 4px 16px rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        {/* Shimmer effect */}
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: `linear-gradient(105deg, transparent 20%, rgba(255, 255, 255, 0.03) 25%, rgba(255, 255, 255, 0.05) 30%, rgba(255, 255, 255, 0.03) 35%, transparent 40%)`,
                          }}
                        />

                        {/* Balance Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <img src={seiIcon} alt="SEI" className="w-6 h-6" />
                            <span className="text-xs font-bold text-purple-300 uppercase tracking-wider">
                              SEI Balance
                            </span>
                          </div>
                          <motion.button
                            onClick={handleRefreshBalance}
                            whileTap={{ scale: 0.9, rotate: 180 }}
                            className="w-7 h-7 rounded-lg flex items-center justify-center"
                            style={{
                              background: 'rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            <svg
                              className={`w-4 h-4 text-purple-300 ${isRefreshing || balanceLoading ? 'animate-spin' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                          </motion.button>
                        </div>

                        {/* Balance Amount */}
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className="font-lilita text-3xl text-white [text-shadow:1px_2px_0px_#000]">
                            {balanceLoading ? '...' : balance.toFixed(4)}
                          </span>
                          <span className="text-sm font-bold text-purple-300">
                            SEI
                          </span>
                        </div>

                        {/* Wallet Address Row */}
                        <div
                          className="flex items-center gap-2 p-2.5 rounded-lg"
                          style={{
                            background: 'rgba(0, 0, 0, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                          }}
                        >
                          <span className="flex-1 text-xs text-purple-200">
                            {user?.address
                              ? formatWalletAddress(user.address, 8)
                              : 'Not connected'}
                          </span>
                          <div className="flex items-center gap-1">
                            {/* Copy Button */}
                            <motion.button
                              onClick={handleCopyAddress}
                              whileTap={{ scale: 0.85 }}
                              className="w-7 h-7 rounded-lg flex items-center justify-center"
                              style={{
                                background: copied
                                  ? 'rgba(34, 197, 94, 0.3)'
                                  : 'rgba(255, 255, 255, 0.1)',
                              }}
                            >
                              {copied ? (
                                <svg
                                  className="w-3.5 h-3.5 text-green-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={3}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-3.5 h-3.5 text-purple-300"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                  />
                                </svg>
                              )}
                            </motion.button>
                            {/* QR Code Button */}
                            <motion.button
                              onClick={() => setView('qrcode')}
                              whileTap={{ scale: 0.85 }}
                              className="w-7 h-7 rounded-lg flex items-center justify-center"
                              style={{
                                background: 'rgba(255, 255, 255, 0.1)',
                              }}
                            >
                              <svg
                                className="w-3.5 h-3.5 text-purple-300"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                                />
                              </svg>
                            </motion.button>
                          </div>
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-4 gap-2 mb-5">
                        {/* Gold */}
                        <motion.div
                          className="flex flex-col items-center p-2.5 rounded-xl"
                          style={{
                            background:
                              'linear-gradient(180deg, #ca8a04 0%, #a16207 100%)',
                            border: '2px solid #92400e',
                            boxShadow: '0 3px 0 #78350f',
                          }}
                          whileHover={{ y: -2 }}
                        >
                          <img
                            src={goldIcon}
                            alt="Gold"
                            className="w-7 h-7 mb-1"
                          />
                          <span className="font-lilita text-sm text-white [text-shadow:0.5px_1px_0px_#000]">
                            {user.gold_balance.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-yellow-100/80 font-medium">
                            Gold
                          </span>
                        </motion.div>

                        {/* Check-ins */}
                        <motion.div
                          className="flex flex-col items-center p-2.5 rounded-xl"
                          style={{
                            background:
                              'linear-gradient(180deg, #7c3aed 0%, #5b21b6 100%)',
                            border: '2px solid #4c1d95',
                            boxShadow: '0 3px 0 #3b0764',
                          }}
                          whileHover={{ y: -2 }}
                        >
                          <svg
                            className="w-7 h-7 mb-1 text-white"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 002 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z" />
                          </svg>
                          <span className="font-lilita text-sm text-white [text-shadow:0.5px_1px_0px_#000]">
                            {user.totalCheckIn}
                          </span>
                          <span className="text-[10px] text-purple-100/80 font-medium">
                            Check-ins
                          </span>
                        </motion.div>

                        {/* Energy */}
                        <motion.div
                          className="flex flex-col items-center p-2.5 rounded-xl"
                          style={{
                            background:
                              'linear-gradient(180deg, #22c55e 0%, #15803d 100%)',
                            border: '2px solid #166534',
                            boxShadow: '0 3px 0 #14532d',
                          }}
                          whileHover={{ y: -2 }}
                        >
                          <img
                            src={energyIcon}
                            alt="Energy"
                            className="w-7 h-7 mb-1"
                          />
                          <span className="font-lilita text-sm text-white [text-shadow:0.5px_1px_0px_#000]">
                            {user.current_energy}/{user.max_energy}
                          </span>
                          <span className="text-[10px] text-green-100/80 font-medium">
                            Energy
                          </span>
                        </motion.div>

                        {/* Score */}
                        <motion.div
                          className="flex flex-col items-center p-2.5 rounded-xl"
                          style={{
                            background:
                              'linear-gradient(180deg, #ec4899 0%, #be185d 100%)',
                            border: '2px solid #9d174d',
                            boxShadow: '0 3px 0 #831843',
                          }}
                          whileHover={{ y: -2 }}
                        >
                          <img
                            src={pointIcon}
                            alt="Score"
                            className="w-7 h-7 mb-1"
                          />
                          <span className="font-lilita text-sm text-white [text-shadow:0.5px_1px_0px_#000]">
                            {user.scores.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-pink-100/80 font-medium">
                            Score
                          </span>
                        </motion.div>
                      </div>

                      {/* Logout Button */}
                      <motion.button
                        onClick={handleLogout}
                        className="relative w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl overflow-hidden"
                        style={{
                          background:
                            'linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #ef4444 100%)',
                          border: '2px solid #7f1d1d',
                          boxShadow: '0 4px 0 #450a0a',
                        }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98, y: 0 }}
                      >
                        <div className="absolute inset-x-1 top-1 h-8 bg-white/20 rounded-lg" />
                        <svg
                          className="w-5 h-5 text-white relative z-10"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        <span className="font-lilita text-xl text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                          Logout
                        </span>
                      </motion.button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="qrcode"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* QR Code Header */}
                    <div
                      className="relative px-5 py-4 flex items-center justify-between"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)',
                        borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
                      }}
                    >
                      <motion.button
                        onClick={() => setView('profile')}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-2"
                      >
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                        <span className="font-lilita text-lg text-white [text-shadow:1px_2px_0px_#000]">
                          Back
                        </span>
                      </motion.button>
                      <motion.div
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleClose}
                        className="cursor-pointer"
                      >
                        <CloseButton />
                      </motion.div>
                    </div>

                    <div className="p-5">
                      {/* Title */}
                      <div className="text-center mb-5">
                        <h3 className="font-lilita text-2xl text-white [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                          Wallet QR Code
                        </h3>
                        <p className="text-sm text-purple-300 mt-1">
                          Scan to send SEI to this address
                        </p>
                      </div>

                      {/* QR Code Container */}
                      <div className="flex justify-center mb-5">
                        <div
                          className="relative p-4 rounded-xl"
                          style={{
                            background: 'white',
                            boxShadow: `
                              0 0 0 3px rgba(139, 92, 246, 0.3),
                              0 8px 24px rgba(0, 0, 0, 0.4)
                            `,
                          }}
                        >
                          {/* Corner decorations */}
                          <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-purple-500 rounded-tl" />
                          <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-purple-500 rounded-tr" />
                          <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-purple-500 rounded-bl" />
                          <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-purple-500 rounded-br" />

                          <QRCodeSVG
                            value={user?.address || ''}
                            size={180}
                            level="H"
                            bgColor="white"
                            fgColor="#1a1a2e"
                          />
                        </div>
                      </div>

                      {/* Network Badge */}
                      <div className="flex justify-center mb-4">
                        <div
                          className="flex items-center gap-2 px-4 py-2 rounded-full"
                          style={{
                            background: 'rgba(34, 197, 94, 0.15)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                          }}
                        >
                          <div
                            className="w-2 h-2 rounded-full animate-pulse"
                            style={{
                              background: '#22c55e',
                              boxShadow: '0 0 8px rgba(34, 197, 94, 0.8)',
                            }}
                          />
                          <span className="text-xs font-bold text-green-400">
                            SEI Network
                          </span>
                        </div>
                      </div>

                      {/* Address Display */}
                      <div
                        className="p-3 rounded-xl mb-5"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        <p className="text-xs text-purple-200 text-center break-all">
                          {user?.address}
                        </p>
                      </div>

                      {/* Copy Button */}
                      <motion.button
                        onClick={handleCopyAddress}
                        className="relative w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl overflow-hidden"
                        style={{
                          background: copied
                            ? 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)'
                            : 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 50%, #7C3AED 100%)',
                          border: copied
                            ? '2px solid #166534'
                            : '2px solid #4c1d95',
                          boxShadow: copied
                            ? '0 4px 0 #14532d'
                            : '0 4px 0 #3b0764',
                        }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98, y: 0 }}
                      >
                        <div className="absolute inset-x-1 top-1 h-8 bg-white/20 rounded-lg" />
                        {copied ? (
                          <>
                            <svg
                              className="w-5 h-5 text-white relative z-10"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={3}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="font-lilita text-xl text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                              Copied!
                            </span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-5 h-5 text-white relative z-10"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                            <span className="font-lilita text-xl text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                              Copy Address
                            </span>
                          </>
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </>
      )}

      {/* Logout Confirmation Modal */}
      <LogoutModal
        isOpen={isLogoutModalOpen}
        onClose={handleCloseLogoutModal}
        onConfirm={handleConfirmLogout}
        isLoading={isLoggingOut}
      />
    </AnimatePresence>
  );
};
